import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, Gauge } from "lucide-react";

export function KmRatesSection() {
  const [rows, setRows] = useState<any[]>([]);
  const [serviceTypes, setServiceTypes] = useState<any[]>([]);
  const [form, setForm] = useState({ service_type_id: "", rate_per_km: "", min_price: "" });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const { toast } = useToast();

  const load = async () => {
    const [r, s] = await Promise.all([
      supabase.from("km_rates").select("*, service_types(name)"),
      supabase.from("service_types").select("*").eq("is_active", true).order("sort_order"),
    ]);
    setRows(r.data || []);
    setServiceTypes(s.data || []);
  };

  useEffect(() => { load(); }, []);

  const handleSave = async () => {
    const payload = { service_type_id: form.service_type_id, rate_per_km: Number(form.rate_per_km), min_price: Number(form.min_price) };
    if (editingId) {
      const { error } = await supabase.from("km_rates").update(payload).eq("id", editingId);
      if (error) { toast({ title: "Error", description: error.message, variant: "destructive" }); return; }
    } else {
      const { error } = await supabase.from("km_rates").insert(payload);
      if (error) { toast({ title: "Error", description: error.message, variant: "destructive" }); return; }
    }
    setForm({ service_type_id: "", rate_per_km: "", min_price: "" });
    setEditingId(null);
    setIsDialogOpen(false);
    load();
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    await supabase.from("km_rates").delete().eq("id", deleteId);
    setDeleteId(null);
    load();
  };

  const openCreate = () => { setForm({ service_type_id: "", rate_per_km: "", min_price: "" }); setEditingId(null); setIsDialogOpen(true); };
  const openEdit = (row: any) => { setForm({ service_type_id: row.service_type_id || "", rate_per_km: String(row.rate_per_km), min_price: String(row.min_price) }); setEditingId(row.id); setIsDialogOpen(true); };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold">Tarifas por KM</h2>
          <p className="text-muted-foreground text-sm mt-1">Tarifas por kilómetro por tipo de servicio <Badge variant="secondary" className="ml-2">{rows.length}</Badge></p>
        </div>
        <Button onClick={openCreate}><Plus className="h-4 w-4 mr-1" /> Agregar</Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {rows.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <Gauge className="h-10 w-10 mb-3 opacity-40" /><p>No hay tarifas configuradas</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tipo de Servicio</TableHead>
                  <TableHead>Tarifa/KM</TableHead>
                  <TableHead>Precio Mínimo</TableHead>
                  <TableHead className="w-[100px] text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map(row => (
                  <TableRow key={row.id}>
                    <TableCell className="font-medium">{(row.service_types as any)?.name}</TableCell>
                    <TableCell className="font-semibold text-green-700">${row.rate_per_km}</TableCell>
                    <TableCell className="text-green-700">${row.min_price}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="icon" variant="ghost" onClick={() => openEdit(row)}><Pencil className="h-4 w-4" /></Button>
                        <Button size="icon" variant="ghost" className="text-destructive" onClick={() => setDeleteId(row.id)}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingId ? "Editar Tarifa" : "Nueva Tarifa"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Tipo de Servicio</Label>
              <Select value={form.service_type_id} onValueChange={v => setForm({ ...form, service_type_id: v })}>
                <SelectTrigger><SelectValue placeholder="Seleccionar tipo de servicio" /></SelectTrigger>
                <SelectContent>{serviceTypes.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Tarifa por KM ($)</Label><Input type="number" step="0.01" value={form.rate_per_km} onChange={e => setForm({ ...form, rate_per_km: e.target.value })} /></div>
            <div><Label>Precio Mínimo ($)</Label><Input type="number" step="0.01" value={form.min_price} onChange={e => setForm({ ...form, min_price: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave}>{editingId ? "Actualizar" : "Crear"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>¿Eliminar tarifa?</AlertDialogTitle><AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={handleDelete}>Eliminar</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
